<template>
  <div class="collapsible-card">
    <div
      class="card-header-toggle"
      @click="toggleCollapse">
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center">
          <i
            :class="[
              'fas fa-chevron-right transition-transform duration-200 mr-2',
              isExpanded ? 'rotate-90' : ''
            ]" />
          <h5 class="card-title m-0">
            <slot name="title" />
          </h5>
        </div>
        <div
          v-if="$slots.headerRight"
          class="flex items-center">
          <slot name="headerRight" />
        </div>
      </div>
    </div>

    <transition
      name="collapse"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
      @after-leave="afterLeave">
      <div
        v-show="isExpanded"
        class="card-content-wrapper">
        <div class="card-content">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  defaultExpanded: {
    type: Boolean,
    default: true
  }
})

const isExpanded = ref(props.defaultExpanded)

const toggleCollapse = () => {
  isExpanded.value = !isExpanded.value
}

const setExpanded = (expanded) => {
  isExpanded.value = expanded
}

// Expose method for parent component
defineExpose({
  setExpanded
})

// Transition hooks for smooth height animation
const enter = (el) => {
  el.style.height = '0'
  el.style.overflow = 'hidden'
  // Force reflow
  // eslint-disable-next-line no-unused-expressions
  el.offsetHeight
  el.style.transition = 'height 0.3s ease-out'
  el.style.height = `${el.scrollHeight}px`
}

const afterEnter = (el) => {
  el.style.height = ''
  el.style.overflow = ''
  el.style.transition = ''
}

const leave = (el) => {
  el.style.height = `${el.scrollHeight}px`
  el.style.overflow = 'hidden'
  // Force reflow
  // eslint-disable-next-line no-unused-expressions
  el.offsetHeight
  el.style.transition = 'height 0.3s ease-out'
  el.style.height = '0'
}

const afterLeave = (el) => {
  el.style.height = ''
  el.style.overflow = ''
  el.style.transition = ''
}
</script>

<style scoped>
.collapsible-card {
  @apply bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4;
}

.card-header-toggle {
  @apply px-5 py-4 bg-gray-50 border-b border-gray-200 cursor-pointer
         hover:bg-gray-100 transition-colors duration-200 select-none;
}

.card-title {
  @apply text-base font-semibold text-gray-900;
}

.card-content-wrapper {
  @apply overflow-hidden;
}

.card-content {
  @apply p-5;
}

/* Remove default margins from slotted content */
.card-content > :deep(*:first-child) {
  margin-top: 0;
}

.card-content > :deep(*:last-child) {
  margin-bottom: 0;
}
</style>
